import i18next, { type Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import coreEn from "./locales/en.json";
import coreFr from "./locales/fr.json";

export const SUPPORTED_LOCALES = ["en", "fr"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = "dl.locale";

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function storedLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Sets the active language.
 *
 * Unlike the Vaadin `LanguageSelector`, which wrote a cookie and reloaded the
 * whole page, this swaps the bundle in place — React re-renders and the user
 * keeps their scroll position, filters and unsaved form state.
 */
export async function setLocale(locale: Locale): Promise<void> {
  await i18next.changeLanguage(locale);
  document.documentElement.lang = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Non-fatal — the change still applies for this session.
  }
}

export interface I18nOptions {
  /** Per-app bundles, merged over the shared ones. */
  resources: Resource;
  /** Usually the account's `settings.language`, lower-cased. */
  initialLocale?: Locale;
}

/**
 * Shared strings — buttons, form validation, login, theme, the common nav
 * entries — ship with this package.
 *
 * This closes a gap in the Vaadin setup, where `web-core` had no bundle of its
 * own and its components silently depended on every host app defining the same
 * keys. Here the shared components carry their own copy, and an app can still
 * override any key by declaring it.
 */
function mergeWithCore(appResources: Resource): Resource {
  const core: Resource = {
    en: { translation: coreEn },
    fr: { translation: coreFr },
  };

  const merged: Resource = {};
  for (const locale of new Set([...Object.keys(core), ...Object.keys(appResources)])) {
    merged[locale] = {
      translation: {
        ...((core[locale]?.translation as object) ?? {}),
        ...((appResources[locale]?.translation as object) ?? {}),
      },
    };
  }
  return merged;
}

export async function initI18n({ resources, initialLocale }: I18nOptions) {
  await i18next.use(initReactI18next).init({
    resources: mergeWithCore(resources),
    lng: initialLocale ?? storedLocale() ?? "en",
    fallbackLng: "en",
    defaultNS: "translation",
    interpolation: {
      // React escapes for us; double-escaping mangles apostrophes in French copy.
      escapeValue: false,
    },
    returnEmptyString: false,
  });

  document.documentElement.lang = i18next.language;
  return i18next;
}

export { i18next };
export { useTranslation, Trans } from "react-i18next";
